import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * Chip — the pill badge used everywhere: filter values, genres, live/pending
 * statuses, suggestion tags. Variants map 1:1 to docs/DESIGN.md Shared
 * Components.
 */
const chip = cva(
  'inline-flex items-center gap-1 rounded-[999px] px-2.5 py-1 font-sans text-[11px] font-semibold leading-none',
  {
    variants: {
      variant: {
        default: 'bg-or-slate-100 text-or-slate-700',
        orange: 'bg-or-orange-100 text-or-orange-800',
        cream: 'bg-or-cream-100 text-or-orange-800',
        outline: 'border border-or-slate-200 bg-white text-or-slate-700',
        blue: 'bg-or-blue-100 text-or-blue-600',
        live: 'bg-or-green-100 text-or-green-700',
        pending: 'bg-or-yellow-200 text-or-orange-800',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface ChipProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof chip> {
  children: ReactNode;
}

export function Chip({ className, variant, children, ...rest }: ChipProps) {
  return (
    <span className={cn(chip({ variant }), className)} {...rest}>
      {children}
    </span>
  );
}
